import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { FaFlask, FaTrash, FaPlus, FaPrint, FaPen, FaTimes } from "react-icons/fa";
import DynamicSelect from "../../DynamicSelect";
import { PathologyPrintSlip } from "../../Pathology/PathologyPrintSlip";

const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const TEST_LIST_URL = "https://api.muktihospital.com/api/pathology";
const SLIP_BASE = "https://api.muktihospital.com/api/pathology-bill/nicu-slip";
const AUTH = { headers: { "x-api-key": API_KEY } };

const parseTests = (tests) => {
  if (Array.isArray(tests)) return tests;
  if (typeof tests === "string") {
    try { return JSON.parse(tests) || []; } catch { return []; }
  }
  return [];
};

const fmtDate = (d) => {
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "N/A"; }
};

const fmtDateTime = (d) => {
  try {
    return new Date(d).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return "N/A"; }
};

const numberToWordsBD = (value) => {
  let num = Math.floor(Number(value) || 0);
  if (num === 0) return "Zero";
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const two = (n) => (n < 20 ? ones[n] : tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : ""));
  const three = (n) => {
    const h = Math.floor(n / 100); const r = n % 100;
    return (h ? ones[h] + " Hundred" + (r ? " " : "") : "") + (r ? two(r) : "");
  };
  let words = "";
  const crore = Math.floor(num / 10000000); num %= 10000000;
  const lakh = Math.floor(num / 100000); num %= 100000;
  const thousand = Math.floor(num / 1000); num %= 1000;
  if (crore) words += three(crore) + " Crore ";
  if (lakh) words += two(lakh) + " Lakh ";
  if (thousand) words += two(thousand) + " Thousand ";
  if (num) words += three(num);
  return words.trim();
};

const emptyRow = () => ({ testId: "", testName: "", amount: 0 });

/**
 * A NICU patient can need tests on several different days, so one patient may
 * have many pathology slips. Previous slips are listed at the top (read-only
 * once paid); the form below always starts empty and creates a NEW slip.
 */
const NicuPathologySlipModal = ({ isOpen, onClose, patient }) => {
  const [testsList, setTestsList] = useState([]);
  const [slips, setSlips] = useState([]);
  const [loadingSlips, setLoadingSlips] = useState(false);
  const [rows, setRows] = useState([emptyRow()]);
  const [editingSlip, setEditingSlip] = useState(null); // set only when an old slip is being corrected
  const [saving, setSaving] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [showPrint, setShowPrint] = useState(false);

  const regNo = patient?.regNo || "";

  // ---- Load the pathology test list ----
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const res = await axios.get(TEST_LIST_URL, AUTH);
        const list = Array.isArray(res.data) ? res.data : res.data.data || [];
        setTestsList(
          list.map((t) => ({ value: t.id.toString(), label: t.testName || "N/A", amount: t.amount || 0 }))
        );
      } catch (err) {
        console.error("Failed to load tests:", err);
        toast.error("Failed to load pathology tests.");
      }
    })();
  }, [isOpen]);

  // ---- Load every slip already created for this patient ----
  const fetchSlips = useCallback(async () => {
    if (!regNo) { setSlips([]); return; }
    try {
      setLoadingSlips(true);
      const res = await axios.get(`${SLIP_BASE}/list?regNo=${encodeURIComponent(regNo)}`, AUTH);
      const bills = res.data.bills || [];
      bills.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setSlips(bills);
    } catch (err) {
      console.error("Failed to load slips:", err);
      setSlips([]);
    } finally {
      setLoadingSlips(false);
    }
  }, [regNo]);

  useEffect(() => {
    if (!isOpen) return;
    setPrintData(null);
    setShowPrint(false);
    setEditingSlip(null);
    setRows([emptyRow()]);
    fetchSlips();
  }, [isOpen, fetchSlips]);

  // When slip data is ready, render then print.
  useEffect(() => {
    if (!printData) return;
    const t = setTimeout(() => {
      window.print();
      setTimeout(() => setPrintData(null), 400);
    }, 120);
    return () => clearTimeout(t);
  }, [printData]);

  if (!isOpen) return null;

  const validTests = rows.filter((r) => r.testId && r.testName);
  const totalAmount = validTests.reduce((s, r) => s + (Number(r.amount) || 0), 0);

  const slipDue = (slip) => {
    if (slip.dueAmount !== undefined && slip.dueAmount !== null) return Number(slip.dueAmount) || 0;
    return (Number(slip.netAmount) || 0) - (Number(slip.paidAmount) || 0);
  };
  const isLocked = (slip) => (Number(slip.paidAmount) || 0) > 0; // money taken → no more edits
  const totalDue = slips.reduce((s, b) => s + Math.max(0, slipDue(b)), 0);

  // Where a test was ordered before — used for a friendly hint, not a block
  const findEarlierSlip = (testId) =>
    slips.find(
      (s) => s.id !== editingSlip?.id && parseTests(s.tests).some((t) => String(t.testId || t.id) === String(testId))
    );

  // ---- Form handlers ----
  const handleTestChange = (index, option) => {
    if (option && rows.some((r, i) => i !== index && r.testId === option.value)) {
      toast.error("This test is already on this slip.");
      return;
    }
    if (option) {
      const earlier = findEarlierSlip(option.value);
      if (earlier) {
        toast.info(
          `Note: "${option.label}" was also ordered on slip ${earlier.billNumber} (${fmtDate(earlier.createdAt)}).`,
          { autoClose: 4000 }
        );
      }
    }
    const next = [...rows];
    next[index] = {
      testId: option ? option.value : "",
      testName: option ? option.label : "",
      amount: option ? Number(option.amount) || 0 : 0,
    };
    setRows(next);
  };

  const addRow = () => setRows([...rows, emptyRow()]);

  const removeRow = (index) => {
    if (rows.length === 1) { setRows([emptyRow()]); return; }
    setRows(rows.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setRows([emptyRow()]);
    setEditingSlip(null);
  };

  const startEdit = (slip) => {
    if (isLocked(slip)) {
      toast.error("This slip is already paid and can no longer be changed.");
      return;
    }
    const tests = parseTests(slip.tests);
    setRows(
      tests.length
        ? tests.map((t) => ({
            testId: (t.testId || t.id || "").toString(),
            testName: t.testName || "",
            amount: Number(t.amount) || 0,
          }))
        : [emptyRow()]
    );
    setEditingSlip(slip);
  };

  const handleDeleteSlip = async (slip) => {
    if (isLocked(slip)) {
      toast.error("A paid slip cannot be deleted.");
      return;
    }
    if (!window.confirm(`Delete slip ${slip.billNumber}? This cannot be undone.`)) return;
    try {
      setSaving(true);
      // If the API names this route differently, change this one line.
      await axios.delete(`${SLIP_BASE}/delete/${slip.id}`, AUTH);
      toast.success("Slip deleted.");
      if (editingSlip?.id === slip.id) resetForm();
      await fetchSlips();
    } catch (err) {
      console.error("Failed to delete slip:", err);
      toast.error(err.response?.data?.error || "Failed to delete the slip.");
    } finally {
      setSaving(false);
    }
  };

  // ---- Print ----
  const buildPrint = (bill) => {
    const tests = parseTests(bill.tests);
    const net = Number(bill.netAmount) || tests.reduce((s, t) => s + (Number(t.amount) || 0), 0);
    const paid = Number(bill.paidAmount) || 0;
    const due = Math.max(0, net - paid);

    const signatureFooter = (
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: "30px", paddingLeft: "10px", paddingRight: "10px" }}>
        <div style={{ textAlign: "center", width: "220px" }}>
          <div style={{ borderTop: "1px solid #333", height: "1px", marginBottom: "8px" }}></div>
          <p style={{ margin: "0", fontWeight: "bold", fontSize: "11pt", textTransform: "capitalize" }}>
            {bill.userName || "NICU Staff"}
          </p>
          <p style={{ margin: "5px 0 0 0", fontSize: "9pt", color: "#555" }}>Prepared By</p>
        </div>
        <div style={{ textAlign: "center", width: "220px" }}>
          <div style={{ borderTop: "1px solid #333", height: "1px", marginBottom: "8px" }}></div>
          <p style={{ margin: "0", fontWeight: "bold", fontSize: "11pt" }}>{patient?.authorizedBy || "N/A"}</p>
          <p style={{ margin: "5px 0 0 0", fontSize: "9pt", color: "#555" }}>Authorized By</p>
        </div>
      </div>
    );

    const billInfo = (
      <>
        <div>
          <p><strong>Bill No:</strong> {bill.billNumber}</p>
          <p><strong>Patient Name:</strong> {bill.patientName || patient?.patientName}</p>
          <p><strong>Mobile:</strong> {bill.phone || "N/A"}</p>
        </div>
        <div>
          <p><strong>Date:</strong> {fmtDateTime(bill.createdAt || new Date())}</p>
          <p><strong>Age:</strong> {patient?.age || "N/A"}</p>
          <p><strong>Ref:</strong> NICU ({patient?.regNo || ""})</p>
        </div>
      </>
    );

    const table = (
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-gray-100">
            <th className="p-2 sm:p-3 border font-semibold">Test Name</th>
            <th className="p-2 sm:p-3 border font-semibold text-right">Amount (৳)</th>
          </tr>
        </thead>
        <tbody>
          {tests.map((row, index) => (
            <tr key={index} className="border-t">
              <td className="p-2 sm:p-3 border">{row.testName || "N/A"}</td>
              <td className="p-2 sm:p-3 border text-right">{Number(row.amount).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );

    const summary = (
      <div className="flex flex-col">
        <div className="flex justify-between"><span>Total:</span><span>৳{net.toFixed(2)}</span></div>
        <div className="flex justify-between"><span>Discount (৳):</span><span>0.00</span></div>
        <div className="flex justify-between"><span>Net Amount:</span><span>৳{net.toFixed(2)}</span></div>
        <div className="flex justify-between"><span>Paid:</span><span>৳{paid.toFixed(2)}</span></div>
        <div className="flex justify-between"><span>Due:</span><span>৳{due.toFixed(2)}</span></div>
      </div>
    );

    const note = (
      <div className="note-box"><strong>Note:</strong> Please pay at the Pathology department.</div>
    );

    return {
      billInfo, table, summary, signatureFooter, note,
      amountInWords: numberToWordsBD(net) + " Taka Only",
      title: "Pathology Slip",
      isPaid: due <= 0,
    };
  };

  const printSlip = (slip) => {
    setPrintData(buildPrint(slip));
    setShowPrint(true);
  };

  // ---- Save ----
  const handleSave = async (thenPrint) => {
    if (validTests.length === 0) { toast.error("Select at least one test."); return; }
    try {
      setSaving(true);
      const testsPayload = validTests.map((r) => ({
        testId: r.testId, testName: r.testName, amount: Number(r.amount) || 0,
      }));
      let savedBill;

      if (editingSlip) {
        const res = await axios.put(
          `${SLIP_BASE}/edit/${editingSlip.id}`,
          { tests: testsPayload, netAmount: totalAmount },
          AUTH
        );
        savedBill = res.data.bill || res.data;
        toast.success(`Slip ${editingSlip.billNumber} updated.`);
      } else {
        const payload = {
          patientName: patient?.patientName || "NICU Patient",
          patientId: patient?.patientId || null,
          tests: testsPayload,
          netAmount: totalAmount,
          regNo: patient?.regNo || null,
          age: patient?.age || null,
          authorizedBy: patient?.authorizedBy || null,
        };
        const res = await axios.post(`${SLIP_BASE}/add`, payload, AUTH);
        savedBill = res.data.bill || res.data;
        toast.success("New pathology slip created.");
      }

      resetForm();
      await fetchSlips();
      if (thenPrint && savedBill) printSlip(savedBill);
    } catch (err) {
      console.error("Failed to save slip:", err);
      toast.error(err.response?.data?.error || "Failed to save the slip.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 print-hidden p-4">
        <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
          {/* ---- Header ---- */}
          <div className="p-5 border-b flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <FaFlask className="text-M-primary-color" />
              <h2 className="text-lg font-bold text-gray-700">Pathology Slips</h2>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1" title="Close">
              <FaTimes />
            </button>
          </div>

          <div className="p-5">
            {/* ---- Patient ---- */}
            <div className="bg-gray-50 rounded-md p-3 mb-4 text-sm grid grid-cols-2 gap-2">
              <div><span className="text-gray-500">Patient:</span> <span className="font-medium">{patient?.patientName || "N/A"}</span></div>
              <div><span className="text-gray-500">Reg No:</span> <span className="font-medium">{patient?.regNo || "N/A"}</span></div>
            </div>

            {/* ---- Existing slips ---- */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-bold text-gray-700">
                  Slips for this patient{" "}
                  {slips.length > 0 && <span className="text-gray-400 font-normal">({slips.length})</span>}
                </h3>
                {totalDue > 0 && (
                  <span className="text-xs font-semibold text-red-600">Total due: ৳{totalDue.toFixed(2)}</span>
                )}
              </div>

              {loadingSlips ? (
                <p className="text-xs text-gray-400 py-3">Loading slips...</p>
              ) : slips.length === 0 ? (
                <p className="text-xs text-gray-400 border border-dashed rounded-md py-4 text-center">
                  No slip has been created for this patient yet.
                </p>
              ) : (
                <div className="overflow-x-auto border rounded-md">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-2 font-semibold">Slip No</th>
                        <th className="text-left p-2 font-semibold">Date</th>
                        <th className="text-left p-2 font-semibold">Tests</th>
                        <th className="text-right p-2 font-semibold">Amount</th>
                        <th className="text-center p-2 font-semibold">Status</th>
                        <th className="text-center p-2 font-semibold">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {slips.map((slip) => {
                        const tests = parseTests(slip.tests);
                        const due = slipDue(slip);
                        const locked = isLocked(slip);
                        const editingThis = editingSlip?.id === slip.id;
                        return (
                          <tr key={slip.id} className={`border-t ${editingThis ? "bg-blue-50" : ""}`}>
                            <td className="p-2 font-medium">{slip.billNumber}</td>
                            <td className="p-2 text-gray-600 whitespace-nowrap">{fmtDate(slip.createdAt)}</td>
                            <td className="p-2 text-gray-600">
                              <span title={tests.map((t) => t.testName).join(", ")}>
                                {tests.length} test{tests.length !== 1 ? "s" : ""}
                              </span>
                            </td>
                            <td className="p-2 text-right font-medium">
                              ৳{(Number(slip.netAmount) || 0).toFixed(2)}
                            </td>
                            <td className="p-2 text-center">
                              <span
                                className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                  due <= 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"
                                }`}
                              >
                                {due <= 0 ? "PAID" : `DUE ৳${due.toFixed(0)}`}
                              </span>
                            </td>
                            <td className="p-2">
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  onClick={() => printSlip(slip)}
                                  className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                                  title="Print this slip"
                                >
                                  <FaPrint size={12} />
                                </button>
                                <button
                                  onClick={() => startEdit(slip)}
                                  disabled={locked}
                                  className={`size-7 rounded-full text-sm inline-flex items-center justify-center transition-all duration-300 ${
                                    locked
                                      ? "bg-gray-100 text-gray-300 cursor-not-allowed"
                                      : "bg-M-Green-color/10 text-M-Green-color hover:bg-M-Green-color hover:text-white"
                                  }`}
                                  title={locked ? "Paid slips cannot be edited" : "Edit this slip"}
                                >
                                  <FaPen size={12} />
                                </button>
                                <button
                                  onClick={() => handleDeleteSlip(slip)}
                                  disabled={locked}
                                  className={`size-7 rounded-full text-sm inline-flex items-center justify-center transition-all duration-300 ${
                                    locked
                                      ? "bg-gray-100 text-gray-300 cursor-not-allowed"
                                      : "bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white"
                                  }`}
                                  title={locked ? "Paid slips cannot be deleted" : "Delete this slip"}
                                >
                                  <FaTrash size={12} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* ---- New / editing slip ---- */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-bold text-gray-700">
                  {editingSlip ? `Editing slip ${editingSlip.billNumber}` : "New slip"}
                </h3>
                {editingSlip && (
                  <button onClick={resetForm} className="text-xs text-M-primary-color hover:underline font-medium">
                    Cancel edit — start a new slip
                  </button>
                )}
              </div>

              <div className="space-y-2">
                {rows.map((row, index) => (
                  <div key={index} className="flex gap-2 items-center">
                    <div className="flex-1">
                      <DynamicSelect
                        options={testsList.filter(
                          (opt) =>
                            opt.value === row.testId ||
                            !rows.some((r, i) => i !== index && r.testId === opt.value)
                        )}
                        value={row.testId ? { value: row.testId, label: row.testName } : null}
                        onChange={(opt) => handleTestChange(index, opt)}
                        isSearchable
                        placeholder="Search & select a test"
                      />
                    </div>
                    <div className="w-24 text-right text-sm font-medium text-gray-600">
                      ৳{Number(row.amount).toFixed(0)}
                    </div>
                    <button
                      onClick={() => removeRow(index)}
                      className="text-red-500 hover:text-red-700 p-2"
                      title="Remove"
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))}
              </div>

              <button
                onClick={addRow}
                className="mt-2 text-sm text-M-primary-color hover:underline flex items-center gap-1 font-medium"
              >
                <FaPlus size={12} /> Add another test
              </button>

              <div className="mt-4 flex justify-end border-t pt-3">
                <div className="text-right">
                  <span className="text-gray-500 text-sm">Total: </span>
                  <span className="text-xl font-black text-M-primary-color">৳{totalAmount.toFixed(2)}</span>
                  <p className="text-xs text-gray-400">Unpaid — pay at Pathology department</p>
                </div>
              </div>
            </div>
          </div>

          {/* ---- Footer ---- */}
          <div className="p-5 border-t flex justify-end gap-3 bg-gray-50 rounded-b-lg">
            <button
              onClick={onClose}
              className="bg-[#E7633D]/90 text-white text-sm py-2 px-5 rounded-md hover:bg-[#E7633D]"
              disabled={saving}
            >
              Close
            </button>
            <button
              onClick={() => handleSave(false)}
              className="bg-gray-600 text-white text-sm py-2 px-5 rounded-md hover:bg-gray-700"
              disabled={saving || validTests.length === 0}
            >
              {saving ? "Saving..." : editingSlip ? "Update Slip" : "Create Slip"}
            </button>
            <button
              onClick={() => handleSave(true)}
              className="bg-M-primary-color/90 text-white text-sm py-2 px-5 rounded-md hover:bg-M-primary-color flex items-center gap-1"
              disabled={saving || validTests.length === 0}
            >
              <FaPrint size={12} /> {editingSlip ? "Update & Print" : "Create & Print"}
            </button>
          </div>
        </div>
      </div>

      {printData && (
          <PathologyPrintSlip
            className="nicu-slip-print"
            title={printData.title}
            billInfo={printData.billInfo}
            table={printData.table}
            summary={printData.summary}
            footer={printData.signatureFooter}
            note={printData.note}
            isPaid={printData.isPaid}
            amountInWords={printData.amountInWords}
          />
      )}
    </>
  );
};

export default NicuPathologySlipModal;
